// The ladder, joined up.
//
// `progression.ts` answers three questions and holds nothing. This file
// is what it answers them *about*: it turns what the operation has done
// into an `OperationHistory`, and it decides when an offer is worth
// making again.
//
// The re-offer rule is the part that took thinking about. DESIGN says an
// offer is dismissible and "re-appears on the next genuine occurrence,
// not on a timer", so a dismissal is stored against a **count of the
// thing that caused it** rather than against a moment. Wave away "make
// it a route?" and it stays away until the round runs on another day.
// Wave away "put offers away?" and it stays away until offers are used
// again and then lapse again. Nothing here counts seconds.

import {
  profileFor,
  collapsibleRungs,
  nextOffer,
  rungEvidence,
  unlockedRungs,
  type Offer,
  type OperationHistory,
  type Rung
} from "@wayfind/core";
import type { FleetState, StoredJob } from "./state";

const DAY = 86400;

/**
 * The operation as the ladder sees it. Nothing in here is a screen.
 *
 * The one piece of arithmetic worth explaining is `finishedUnixSeconds`,
 * because `rungEvidence` reads two jobs' spans overlapping as "two jobs
 * ran at once" — which is rung 2, the moment a job stops being the phone
 * in your hand.
 *
 * A job the operator never closed is not a job that runs forever. It
 * ends when its ticket does, at the latest. And **two jobs held by the
 * same phone are one phone**: a rider carrying two bags is exactly the
 * operation DESIGN says can live at rung 0 forever, so jobs sharing a
 * holder are laid end to end rather than reported as concurrent. Two
 * jobs held by *different* people is the real signal, and it survives
 * this untouched.
 */
export function operationHistory(state: FleetState): OperationHistory {
  const byHolder = new Map<string, number[]>();
  const ordered = state.jobs.slice().sort((a, b) => a.createdUnixSeconds - b.createdUnixSeconds);
  for (const job of ordered) {
    if (!job.heldBy) continue;
    const holder = job.heldBy;
    const starts = byHolder.get(holder) ?? [];
    starts.push(job.createdUnixSeconds);
    byHolder.set(holder, starts);
  }

  function endOf(job: (typeof ordered)[number]): number | null {
    if (job.finishedUnixSeconds != null) return job.finishedUnixSeconds;
    // A job nobody is holding is not being driven. It is a job waiting
    // for somebody, which is rung 4's evidence and not rung 2's.
    if (!job.heldBy) return job.createdUnixSeconds;
    const next = (byHolder.get(job.heldBy) ?? [])
      .find(start => start > job.createdUnixSeconds);
    if (next != null) return next;
    // A ticket that has expired is a job that is over, whether or not
    // anybody remembered to say so.
    return job.notAfter;
  }

  return {
    profile: state.profile ?? "courier",
    jobs: ordered.map(job => ({
      id: job.id,
      createdUnixSeconds: job.createdUnixSeconds,
      stopCount: job.stops.length,
      planKey: job.planKey,
      deviceId: job.deviceId,
      driverless: job.driverless,
      startedUnixSeconds: job.startedUnixSeconds,
      finishedUnixSeconds: endOf(job),
      usedRungs: job.usedRungs
    })),
    devices: state.devices.map(device => ({
      id: device.id,
      enrolledUnixSeconds: device.enrolledUnixSeconds,
      retiredUnixSeconds: device.retiredUnixSeconds
    })),
    operatorRequested: state.operatorRequested,
    collapsed: state.collapsed
  };
}

/**
 * How many times the thing that unlocks a rung has actually happened.
 *
 * This is the counter a dismissal is pinned to. It only ever goes up, and
 * it goes up exactly when the operator does the thing again — which is
 * what "the next genuine occurrence" means.
 */
export function unlockOccurrences(state: FleetState, rung: Rung, now: number): number {
  const jobs = state.jobs.filter(job => job.createdUnixSeconds <= now);
  switch (rung) {
    case 1:
      return jobs.filter(job => job.stops.length >= 2).length;
    case 2:
      return state.devices.filter(device => device.enrolledUnixSeconds <= now).length;
    case 3:
      return repeatedDays(jobs);
    case 4:
      return jobs.filter(job => job.driverless).length;
    case 5:
      return state.devices.filter(device => device.enrolledUnixSeconds <= now).length;
    default:
      return 0;
  }
}

/** How many separate days the most-repeated round has gone out on. */
function repeatedDays(jobs: StoredJob[]): number {
  const byPlan = new Map<string, Set<number>>();
  for (const job of jobs) {
    if (!job.planKey) continue;
    const days = byPlan.get(job.planKey) ?? new Set<number>();
    days.add(Math.floor(job.createdUnixSeconds / DAY));
    byPlan.set(job.planKey, days);
  }
  let most = 0;
  for (const days of byPlan.values()) most = Math.max(most, days.size);
  return most;
}

/**
 * How many times a rung has been *used*, which is what a collapse offer
 * is pinned to. Say "no, keep it" and the app does not ask again until
 * the rung has been used once more and then gone quiet once more.
 */
export function collapseOccurrences(state: FleetState, rung: Rung): number {
  return state.jobs.filter(job => job.usedRungs.includes(rung)).length;
}

function acknowledgementKey(offer: Offer): string {
  return `${offer.kind}:${offer.rung}`;
}

function occurrencesFor(state: FleetState, offer: Offer, now: number): number {
  return offer.kind === "unlock"
    ? unlockOccurrences(state, offer.rung, now)
    : collapseOccurrences(state, offer.rung);
}

/** Rungs whose unlock offer has been seen and not yet re-earned. */
function acknowledgedUnlocks(state: FleetState, now: number): Rung[] {
  const out: Rung[] = [];
  for (const rung of [1, 2, 3, 4, 5] as Rung[]) {
    const ack = state.acknowledged[`unlock:${rung}`];
    if (!ack) continue;
    if (ack.settled || unlockOccurrences(state, rung, now) <= ack.occurrences) out.push(rung);
  }
  return out;
}

export interface LadderView {
  history: OperationHistory;
  /** Rungs the operation has earned. Evidence, not interface. */
  unlocked: Rung[];
  /**
   * Rungs whose concepts are actually on screen.
   *
   * Earning a rung is not the same as being shown it. The app offers,
   * and "Not now" is a real answer: the panel stays away until the
   * operator takes the offer up or the need recurs and they are asked
   * again. Rungs 0 and 1 are never gated — an address and a sequence are
   * not vocabulary anyone has to agree to.
   */
  revealed: Rung[];
  reached: Rung;
  /** The one thing worth putting in front of the operator, or nothing. */
  offer: Offer | null;
  evidence: ReturnType<typeof rungEvidence>;
  collapsible: Rung[];
}

export function ladderView(state: FleetState, now: number): LadderView {
  const history = operationHistory(state);
  const unlocked = unlockedRungs(history, now);
  const acknowledged = acknowledgedUnlocks(state, now);
  // School transport is granted rung 3 by its profile, so there is
  // nothing to offer: a term is the only shape the operation has, and
  // "you have sent this round three days running" would be a sentence
  // about something that never happened. It is simply there.
  const granted = profileFor(history.profile).defaults.grantsRung === 3;
  let offer = nextOffer(history, now, granted ? [...acknowledged, 3 as Rung] : acknowledged);
  // `nextOffer` has no opinion about dismissed *collapse* offers, because
  // it holds no storage. This is that opinion.
  if (offer?.kind === "collapse") {
    const ack = state.acknowledged[acknowledgementKey(offer)];
    if (ack && (ack.settled || collapseOccurrences(state, offer.rung) <= ack.occurrences)) offer = null;
  }
  const revealed = unlocked.filter(rung =>
    rung <= 1
    || (rung === 3 && granted)
    || state.acknowledged[`unlock:${rung}`]?.settled === true);

  return {
    history,
    unlocked,
    revealed,
    reached: unlocked.length ? unlocked[unlocked.length - 1]! : 0,
    offer,
    evidence: rungEvidence(history, now),
    collapsible: collapsibleRungs(history, now)
  };
}

/** Whether a rung's concepts may appear in the interface at all. */
export function has(view: LadderView, rung: Rung): boolean {
  return view.revealed.includes(rung);
}

function remember(
  state: FleetState,
  offer: Offer,
  now: number,
  settled: boolean
): FleetState {
  return {
    ...state,
    acknowledged: {
      ...state.acknowledged,
      [acknowledgementKey(offer)]: {
        occurrences: occurrencesFor(state, offer, now),
        atUnixSeconds: now,
        ...(settled ? { settled: true } : {})
      }
    }
  };
}

/** Waved away. It comes back the next time the need genuinely recurs. */
export function dismissOffer(state: FleetState, offer: Offer, now: number): FleetState {
  return remember(state, offer, now, false);
}

/**
 * Taking an offer up. An unlock is only ever acknowledged — the rung was
 * already earned, and the offer was the app saying so. A collapse
 * actually tucks the rung away, and it stays away until it is earned
 * again.
 */
export function acceptOffer(state: FleetState, offer: Offer, now: number): FleetState {
  const acknowledged = remember(state, offer, now, true);
  if (offer.kind === "unlock") return acknowledged;
  return {
    ...acknowledged,
    collapsed: acknowledged.collapsed.includes(offer.rung)
      ? acknowledged.collapsed
      : [...acknowledged.collapsed, offer.rung]
  };
}
